"use client";

import { memo, useMemo } from "react";
import type { ChannelEvent } from "../hooks/useMessages";
import type { WorkSession } from "../hooks/useWork";
import { WorkBubble } from "./WorkBubble";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeSanitize from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import type { Root } from "hast";
import { visit } from "unist-util-visit";

// Rehype plugin: add target="_blank" rel="noopener noreferrer" to all <a> tags
function rehypeExternalLinks() {
  return (tree: Root) => {
    visit(tree, "element", (node) => {
      if (node.tagName === "a") {
        node.properties ??= {};
        node.properties.target = "_blank";
        node.properties.rel = "noopener noreferrer";
      }
    });
  };
}

// ── Markdown → HTML pipeline (built once, reused forever) ──────────────
const processor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkRehype)
  .use(rehypeSanitize)
  .use(rehypeExternalLinks)
  .use(rehypeStringify);

// Module-level cache: eventId → HTML string.
// Survives virtualizer unmount/remount cycles — the whole point.
const htmlCache = new Map<string, string>();

function markdownToHtml(eventId: string, text: string): string {
  const cached = htmlCache.get(eventId);
  if (cached !== undefined) return cached;
  // processSync is fine here — the text is small (a single chat message)
  const html = String(processor.processSync(text));
  htmlCache.set(eventId, html);
  return html;
}

interface Props {
  event: ChannelEvent;
  isOwnMessage: boolean;
  currentUserId: string | null;
  agentName: string | null;
  senderDisplayName: string | null;
  workSession?: WorkSession;
  onLoadWorkUpdates?: (sessionId: string) => void;
}

function formatSystemEvent(event: ChannelEvent, currentUserId: string | null): string {
  const body = event.body as Record<string, unknown>;

  if (event.eventType === "member_joined") {
    const invitedByUserId = body.invitedByUserId as string | undefined;
    const invitedName = body.invitedName as string | undefined;
    const invitedByName = body.invitedByName as string | undefined;
    const memberId = body.memberId as string | undefined;

    if (invitedByUserId) {
      const isYouJoining = memberId === currentUserId;
      const isYouInviting = invitedByUserId === currentUserId;

      if (isYouJoining) {
        return `you joined via invitation from ${invitedByName ?? "someone"}`;
      }
      if (isYouInviting) {
        const memberName = invitedName ?? "Someone";
        return `${memberName} joined`;
      }
      const memberName = invitedName ?? "Someone";
      return `${memberName} joined via invitation from ${invitedByName ?? "someone"}`;
    }
    return "A member joined the channel";
  }

  if (event.eventType === "member_left") {
    const memberName = body.memberName as string | undefined;
    const removedBy = body.removedBy as string | undefined;

    if (memberName && removedBy) {
      return `${memberName} was removed by ${removedBy}`;
    }
    if (memberName) {
      return `${memberName} left the channel`;
    }
    return "A member left the channel";
  }

  if (event.eventType === "invite_created") {
    const inviteeName = body.inviteeName as string | undefined;
    const invitedByUserId = body.invitedByUserId as string | undefined;
    const invitedByName = body.invitedByName as string | undefined;
    const isYou = invitedByUserId === currentUserId;

    return isYou
      ? `you invited ${inviteeName ?? "someone"}`
      : `${invitedByName ?? "someone"} invited ${inviteeName ?? "someone"}`;
  }

  return event.eventType;
}

export const MessageBubble = memo(function MessageBubble({ event, isOwnMessage, currentUserId, agentName, senderDisplayName, workSession, onLoadWorkUpdates }: Props) {
  // Work events render as WorkBubble
  if (event.eventType === "work") {
    const session: WorkSession = workSession ?? {
      sessionId: (event.body as any)?.sessionId ?? event.id,
      channelId: event.channelId,
      agentId: event.senderAgentId,
      eventId: event.id,
      startedAt: (event.body as any)?.startedAt ?? event.createdAt,
      completedAt: (event.body as any)?.completedAt ?? null,
      summary: (event.body as any)?.summary ?? null,
      updates: [],
      updatesLoaded: false,
    };
    return (
      <WorkBubble
        session={session}
        agentName={agentName}
        onLoadUpdates={onLoadWorkUpdates ?? (() => {})}
      />
    );
  }

  // System events (member_joined, member_left, invite_created)
  if (event.eventType !== "message") {
    return (
      <div
        style={{
          textAlign: "center",
          fontSize: "0.7rem",
          color: "var(--color-text-faint)",
          padding: "0.25rem 0",
        }}
      >
        {formatSystemEvent(event, currentUserId)}
      </div>
    );
  }

  const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "/api";

  const rawAttachments = (event.body as any)?.attachments as Array<{
    id: string; filename: string; mimeType: string; url: string;
  }> | undefined;
  const images = rawAttachments?.filter(a => a.mimeType.startsWith("image/")) ?? [];
  const files = rawAttachments?.filter(a => !a.mimeType.startsWith("image/")) ?? [];

  const resolveUrl = (url: string) => {
    if (url.startsWith("/v1/")) return API_URL + url.slice(3);
    return url;
  };

  const time = new Date(event.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const senderLabel =
    event.senderType === "agent"
      ? (agentName ?? `Agent`)
      : (senderDisplayName ?? (isOwnMessage ? "You" : "User"));

  // Parse markdown once per event — cached at module level so
  // virtualizer unmount/remount cycles don't re-parse.
  const html = event.body?.text
    ? markdownToHtml(event.id, event.body.text)
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isOwnMessage ? "flex-end" : "flex-start",
        padding: "0.15rem 0",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <div
        style={{
          fontSize: "0.65rem",
          color: "var(--color-text-tertiary)",
          marginBottom: 2,
          paddingLeft: isOwnMessage ? 0 : 2,
          paddingRight: isOwnMessage ? 2 : 0,
        }}
      >
        {senderLabel}
      </div>
      <div
        className="message-content"
        style={{
          maxWidth: "70%",
          padding: "0.4rem 0.65rem",
          borderRadius: 8,
          background: isOwnMessage ? "var(--color-msg-own-bg)" : "var(--color-msg-other-bg)",
          border: `1px solid ${isOwnMessage ? "var(--color-msg-own-border)" : "var(--color-msg-other-border)"}`,
          fontSize: "0.85rem",
          color: "var(--color-text)",
          lineHeight: 1.4,
          wordBreak: "break-word",
        }}
      >
        {images.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: event.body?.text ? "0.35rem" : 0 }}>
            {images.map(img => (
              <a key={img.id} href={resolveUrl(img.url)} target="_blank" rel="noopener noreferrer">
                <img
                  src={resolveUrl(img.url)}
                  alt={img.filename}
                  style={{
                    maxWidth: "100%",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "block",
                  }}
                />
              </a>
            ))}
          </div>
        )}
        {html ? (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : null}
        {files.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: event.body?.text || images.length > 0 ? "0.35rem" : 0 }}>
            {files.map(f => (
              <a
                key={f.id}
                href={resolveUrl(f.url)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  background: "var(--color-file-link-bg)",
                  padding: "0.2rem 0.5rem",
                  borderRadius: 4,
                  fontSize: "0.75rem",
                  color: "var(--color-accent)",
                  textDecoration: "none",
                }}
              >
                {f.filename}
              </a>
            ))}
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: "0.6rem",
          color: "var(--color-text-faintest)",
          marginTop: 2,
          paddingLeft: isOwnMessage ? 0 : 2,
          paddingRight: isOwnMessage ? 2 : 0,
        }}
      >
        {time}
      </div>
    </div>
  );
});
